/**
 * Marqueurs juridiques — source unique, partagée add-in Word ↔ canvas web.
 *
 * POURQUOI ce module (#1001) : jusqu'ici l'add-in Word (`formattingRules.ts`) et le parseur
 * web (`structuralParser.ts`) reconnaissaient les MÊMES marqueurs juridiques avec deux jeux
 * de règles maintenus séparément. Un durcissement de regexp n'a touché qu'un côté et a produit
 * les écarts de mise en forme corrigés en tâche 5 du plan de convergence (salutation non
 * détectée → mode compact jamais éteint → tout le corps du courrier en spacing nul). La seule
 * garantie structurelle qu'un tel écart ne se reproduise pas est de n'avoir qu'une définition.
 *
 * Périmètre : uniquement des fonctions PURES (chaîne → booléen / chaîne). L'APPLICATION reste
 * spécifique à chaque hôte — Office.js côté add-in, Document API SuperDoc côté canvas — et ne
 * migre pas ici (décision D8 de la spec de convergence).
 *
 * Le comportement figé est celui de l'add-in : c'est la référence posée par le porteur. La
 * table de vérité `apps/addin-word/src/services/__tests__/marqueurs-reference.test.ts` verrouille
 * le verdict de chaque prédicat sur un corpus de lignes représentatives.
 */

// ─── Primitives de nettoyage ────────────────────────────────────────────────

/**
 * Retire les balises de structure (`[PARA]`, `[/TITRE]`, `[TITRE1]`…) pour la détection.
 * Gère aussi la variante malformée que le LLM produit parfois (`>` au lieu de `]`).
 */
import { startsWithPieceCitation } from './pieceCitation.js';

export function cleanBalises(text: string): string {
  // Les suffixes de réplique qualifient le paragraphe terminé. Ils doivent
  // être invisibles aussi dans les prédicats de marqueurs, pas seulement dans
  // le parseur structurel (#1012).
  return text.replace(/\[\/?\w+\d*(?::(?:NOUVEAU|ENRICHI))?[\]>]/gi, '').trim();
}

/**
 * Retire tout le formatage markdown inline avant détection de délimiteur.
 * Liens `[texte](url)`, gras `**texte**`, italique `*texte*` / `_texte_`.
 * Nécessaire pour que la détection fonctionne quel que soit le formatage produit par le LLM.
 */
export function stripMarkdownFormatting(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // [texte](url) → texte
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **gras** → gras
    .replace(/\*([^*]+)\*/g, '$1') // *italique* → italique
    .replace(/_([^_]+)_/g, '$1') // _italique_ → italique
    .trim();
}

/**
 * Nettoie le texte d'un marqueur de structure en retirant les `**` d'enrobage,
 * pour un affichage cohérent quel que soit le format d'entrée.
 */
export function cleanMarkerText(text: string): string {
  return text
    .trim()
    .replace(/^\*\*/, '')
    .replace(/\*\*$/, '')
    .trim();
}

/**
 * Retire les marqueurs de formatage inline injectés par le buffer de streaming autour des
 * citations (`[ITALIC_START]…[ITALIC_END]`) et du gras (`[BOLD_START]…[BOLD_END]`).
 *
 * POURQUOI : ces marqueurs sont une affaire de paragraphe — seul le chemin d'insertion de
 * paragraphe les consomme pour construire des runs italique/gras. Un titre s'insère en un seul
 * run et ne les parse jamais, donc un titre qui en portait les imprimait littéralement
 * (bug 2026-07-10). On les retire au point de rendu des titres.
 */
export function stripInlineFormattingMarkers(text: string): string {
  return text.replace(/\[(?:ITALIC|BOLD)_(?:START|END)\]/g, '');
}

// ─── Citations ──────────────────────────────────────────────────────────────

/** Segment de texte porteur de ses marques de run. */
export interface TextSegment {
  text: string;
  italic: boolean;
  bold: boolean;
}

/**
 * Vrai si le caractère est un guillemet, quel que soit son type.
 *
 * POURQUOI U+2018 / U+2019 sont EXCLUS : en français ce sont des apostrophes
 * (l'article, d'une, qu'il). Les traiter comme des guillemets ferait basculer l'italique
 * à chaque apostrophe. Seuls les guillemets DOUBLES et les chevrons comptent.
 */
export function isAnyQuoteChar(char: string): boolean {
  return (
    char === '"' || // guillemet droit
    char === '«' || // « chevron ouvrant
    char === '»' || // » chevron fermant
    char === '“' || // “ guillemet courbe ouvrant
    char === '”' // ” guillemet courbe fermant
  );
}

// ─── Courrier / mise en demeure ────────────────────────────────────────────

/**
 * Detect EXPÉDITEUR marker (already exists in code but adding explicit export)
 * Note: This function already appears in isAnyDocumentMarker but we export it separately
 * 🆕 ENHANCED: Now handles heading markers (###, ##) in addition to ** markers
 */
export function isExpediteurBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Examples: "### **EXPÉDITEUR :", "## **EXPÉDITEUR :", "**EXPÉDITEUR :", "EXPÉDITEUR :"
  return /^(?:#{1,6}\s*)?(\*\*)?EXP[ÉE]DITEUR\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect DESTINATAIRE marker (already exists in code but adding explicit export)
 * Note: This function already appears in isAnyDocumentMarker but we export it separately
 * 🆕 ENHANCED: Now handles heading markers (###, ##) in addition to ** markers
 */
export function isDestinataireBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Examples: "### **DESTINATAIRE :", "## **DESTINATAIRE :", "**DESTINATAIRE :", "DESTINATAIRE :"
  return /^(?:#{1,6}\s*)?(\*\*)?DESTINATAIRE\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "Par lettre recommandée..." trigger for mise en demeure table creation.
 * Why very broad: the LLM may abbreviate, rephrase, or omit parts of the formula.
 * Any line starting with "Par lettre" or "Par courrier" is enough to trigger.
 */
export function isParLettreRecommandeeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  const cleanedMarkers = cleanMarkerText(cleaned);
  // Matches any line starting with "Par lettre" or "Par courrier" (rest is free-form)
  return /^(\*\*)?Par\s+(lettre|courrier)\b/i.test(cleanedMarkers);
}

export function isMadameMonsieurBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?A?\s*Madame,\s*Monsieur(\*\*)?/i.test(cleaned);
}

export function isObjetBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // 🆕 ENHANCED: Now handles heading markers (###, ##) and ** markers
  // Examples: "### **Objet :", "## **Objet :", "**Objet :", "OBJET :"
  return /^(?:#{1,6}\s*)?(\*\*)?OBJET(\s*\([^)]+\))?\s*:(\*\*)?/i.test(cleaned);
}

/**
 * Detect "Objet de la demande :" marker
 * This is a special delimiter in assignation documents
 * NOTE: This marker is bold + underline, line break AFTER only
 * IMPORTANT: This marker should EXIT reduced spacing mode
 */
export function isObjetDeLaDemandeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "Objet de la demande :", with optional ** markers
  return /^(\*\*)?Objet\s+de\s+la\s+demande\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "Pièces jointes :" marker
 * When detected, should trigger same behavior as BORDEREAU for subsequent Pièces n°
 * (i.e., Pièces after this marker are left-aligned and normal, not bold + right-aligned)
 */
export function isPiecesJointesBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?PI[EÈ]CES?\s+JOINTES?\s*:(\*\*)?$/i.test(cleaned);
}

// ─── Structure du document ─────────────────────────────────────────────────

export function isPlaiseLine(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Case-insensitive: LLMs sometimes produce "Plaise au conseil de prudhommes," (title case)
  // instead of the prescribed "PLAISE AU CONSEIL DE PRUD'HOMMES DE [VILLE]". Any capitalization
  // variant of this opening formula is the same legal delimiter — no legitimate French legal
  // sentence starts with "Plaise" outside this formula.
  return /^(\*\*)?PLAISE\b/i.test(cleaned.replace(/\*\*$/, ''));
}

/**
 * Detect "PAR CES MOTIFS" standalone line (not preceded by ## or III. etc.)
 * This is a special legal document delimiter that can be configured in cabinet settings
 * for custom formatting (centered, larger font, etc.)
 */
export function isParCesMotifsLine(text: string): boolean {
  const cleaned = cleanBalises(text).trim();

  // Must NOT be preceded by heading markers (##) or roman numerals (III.)
  // Only match standalone "PAR CES MOTIFS" (with optional ** markers)
  const hasHeadingPrefix = /^#{1,6}\s+/.test(cleaned);
  const hasRomanPrefix = /^[IVX]+[.)]\s+/i.test(cleaned);

  if (hasHeadingPrefix || hasRomanPrefix) {
    return false;
  }

  // Match "PAR CES MOTIFS" with optional ** markers around it
  // Case-insensitive to catch variations
  return /^(?:\*\*)?PAR\s+CES\s+MOTIFS(?:\*\*)?[,:\s]*$/i.test(cleaned);
}

/**
 * Clean PAR CES MOTIFS text by removing markdown markers
 */
export function cleanParCesMotifsText(text: string): string {
  return text.trim()
    .replace(/^#{1,6}\s*/, '')  // Remove heading markers
    .replace(/^\*\*/, '')       // Remove leading **
    .replace(/\*\*$/, '')       // Remove trailing **
    .replace(/[,:\s]*$/, '')    // Remove trailing punctuation
    .trim();
}

/**
 * Detect asterisk separator line (* or *** or * * * or similar)
 * These are visual separators used in legal documents that should be centered
 * Matches: "*", "***", "* * *", "* *", etc. (1 or more asterisks)
 *
 * A bare "**" is explicitly NOT a separator: it is the closing marker of a bold
 * span whose opening "**" was left on the previous paragraph (the LLM wrapped a
 * "[/PARA]" delimiter inside the bold, e.g. "**En droit,[/PARA]**"). Before this
 * exclusion, that orphan rendered as a centered "**" in the middle of the acte.
 * See repairOrphanBoldMarkers(), which normally consumes it before we get here.
 */
export function isAsteriskSeparator(text: string): boolean {
  const cleaned = cleanBalises(text).trim();
  if (cleaned === '**') return false;
  // Match 1+ asterisks with optional spaces between them
  // Examples: *, ***, ** *, * * *, etc.
  // Single asterisk: exactly one *
  // Multiple asterisks: 2+ asterisks with optional spaces
  return /^\*+$/.test(cleaned) || (/^\*[\s*]*\*[\s*]*\*?$/.test(cleaned) && cleaned.replace(/\s/g, '').length >= 2);
}

/**
 * Repair a bold span broken across a paragraph boundary.
 *
 * The plan-détaillé prompt tells the LLM to terminate every line with "[/PARA]"
 * AND to bold the rubric labels. The model routinely wraps the whole template
 * line, delimiter included — "**En droit,[/PARA]**" — so the streaming buffer
 * splits at the delimiter and emits two paragraphs, "**En droit," then "**".
 * Both halves then carry an odd number of "**", which `hasValidBoldPatterns()`
 * rejects, so the asterisks were rendered literally in the acte (bug 2026-07-10).
 *
 * Rules, applied to a single already-split line:
 * - even number of "**" (including zero) → nothing to repair, returned as-is
 * - only asterisks left over → orphan closing marker, returned empty (caller drops the paragraph)
 * - opens with "**" but never closes → close it: the whole line was meant to be bold
 * - closes with "**" but never opened → open it: same intent, opener lost upstream
 * - orphan marker in the middle → intent is unrecoverable, drop the marker rather
 *   than print it (a literal "**" in a pleading is always wrong)
 *
 * @param text A single line, already stripped of custom delimiters.
 * @returns The line with balanced bold markers, or '' if it held nothing but markers.
 */
export function repairOrphanBoldMarkers(text: string): string {
  const doubleStarCount = (text.match(/\*\*/g) || []).length;
  if (doubleStarCount % 2 === 0) return text;

  const trimmed = text.trim();
  if (/^\*+$/.test(trimmed)) return '';
  if (trimmed.startsWith('**')) return `${trimmed}**`;
  if (trimmed.endsWith('**')) return `**${trimmed}`;
  return trimmed.replace(/\*\*/, '').trim();
}

/**
 * Detect first-level Roman numeral section (I./I)) lines.
 * Matches optional heading markers, then I followed by ., -, ), ] and optional uppercase title.
 * 🆕 ENHANCED: Now handles space before separator and different dash types (-, –, —)
 * Examples: "I. ", "I - ", "I – ", "I — ", "I) ", "I – RAPPEL DES FAITS"
 */
export function isRomanSectionLine(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Allow optional space before separator, support multiple dash types (ASCII -, en dash –, em dash —)
  return /^(?:#{1,6}\s*)?I\s*[.\-–—)\]]\s*([A-ZÀ-ÿ][A-ZÀ-ÿ\s]*)?/i.test(cleaned);
}

/**
 * Detect "Exposé des faits" section header
 * This section should exit reduced spacing mode to restore normal paragraph spacing
 */
export function isExposeDesFaitsBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(?:#{1,6}\s*)?(?:\*\*)?(?:I+[.\-)\]]\s*)?EXPOS[ÉE]\s+DES\s+FAITS(?:\*\*)?/i.test(cleaned);
}

/**
 * Detect "Rappel des faits" section header
 * This section should exit reduced spacing mode to restore normal paragraph spacing
 */
export function isRappelDesFaitsBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(?:#{1,6}\s*)?(?:\*\*)?(?:I+[.\-)\]]\s*)?RAPPEL\s+DES\s+FAITS(?:\*\*)?/i.test(cleaned);
}

/**
 * 🆕 Detect "Demande de mise en liberté" document title
 * Documents with this title should not have a page break before Roman sections (I. RAPPEL DES FAITS)
 * Matches: "DEMANDE DE MISE EN LIBERTÉ" only (not just "MISE EN LIBERTÉ")
 */
export function isMiseEnLiberteTitle(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Match only: "DEMANDE DE MISE EN LIBERTÉ" (DEMANDE DE is required)
  return /DEMANDE\s+DE\s+MISE\s+EN\s+LIBERT[ÉE]/i.test(cleaned);
}

/**
 * 🆕 Detect "Demande de renvoi d'audience" document title
 * Documents with this title should:
 * - Have centered underlined title (not boxed) like Mise en demeure
 * - Not have a page break before Roman sections (I.)
 * - Not apply reduced spacing after "Objet :" marker
 * Matches: "DEMANDE DE RENVOI D'AUDIENCE", "RENVOI D'AUDIENCE"
 */
export function isRenvoiAudienceTitle(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Match variations with flexible apostrophe handling
  return /(?:DEMANDE\s+DE\s+)?RENVOI\s+D['']AUDIENCE/i.test(cleaned);
}

/**
 * 🆕 Detect "Demande d'ajournement" document title
 * Documents with this title should:
 * - Have centered underlined title (not boxed) like Mise en demeure
 * - Not have a page break before Roman sections (I.)
 * - Not apply reduced spacing after "Objet :" marker
 * Matches: "DEMANDE D'AJOURNEMENT", "AJOURNEMENT"
 */
export function isAjournementTitle(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Match variations with flexible apostrophe handling
  return /(?:DEMANDE\s+D[''])?AJOURNEMENT/i.test(cleaned);
}

/**
 * 🆕 Detect "REQUÊTE EN" document titles (various types)
 * Documents with these titles should:
 * - Have centered BOXED title (standard format)
 * - Not have a page break before Roman sections (I.)
 * - Not apply reduced spacing after "Objet :" marker
 *
 * Matches all "REQUÊTE EN" variations including:
 * - REQUÊTE EN CONVERSION DE PEINE EN TRAVAIL D'INTÉRÊT GÉNÉRAL / JOURS-AMENDE
 * - REQUÊTE EN AMÉNAGEMENT DE PEINE (all types: DDSE, Placement extérieur, Semi-liberté)
 * - REQUÊTE EN LIBÉRATION CONDITIONNELLE
 * - REQUÊTE EN LIBÉRATION SOUS CONTRAINTE
 * - REQUÊTE EN SUSPENSION DE PEINE / FRACTIONNEMENT DE PEINE
 * - REQUÊTE EN RÉVISION D'UNE CONDAMNATION PÉNALE DÉFINITIVE
 */
export function isRequeteEnTitle(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Match specific "REQUÊTE EN" document types that should skip page break before Roman sections
  // - "Requête en aménagement de peine" (semi-liberté, bracelet électronique, etc.)
  // - "Requête en nullité"
  // NOT: "Requête en référé" (should have page break before I.)
  return /REQU[EÊ]TE\s+EN\s+(AM[ÉE]NAGEMENT|NULLIT[ÉE])/i.test(cleaned);
}

export function isTresImportantBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches all possible combinations of accented/non-accented variants, colon is optional
  return /^(\*\*)?TR[EÈ]S\s+IMPORTANT\s*:?(\*\*)?$/i.test(cleaned);
}

// ─── Bordereau et pièces ───────────────────────────────────────────────────

/**
 * Corrects the systematic "bordereau" misspelling produced by the primary redaction model.
 * GLM 5.2 (`tensorix/z-ai/glm-5.2`) writes "BORDEREAAU" (doubled A) in ~82% of the actes
 * where it emits a bordereau, whereas Sonnet — reading the exact same prompts and case data —
 * never does (control arm: 0 typos across every acte type). The double-A is therefore
 * generated by the model, not present in any prompt/template (verified 2026-07-28 against the
 * generated_documents table). Left uncorrected it causes two defects: (1) a visible spelling
 * error in the acte's bordereau heading, and (2) isBordereauLine() stops matching, so
 * BordereauStateManager.enterBordereau() never fires and the pièces listed under the bordereau
 * keep the "Pièce (corps)" italic style instead of "Pièce (bord.)". We normalise the model
 * output BEFORE bordereau detection and insertion. Case-preserving (the captures keep the
 * original casing) and idempotent (a no-op on already-correct text).
 */
export function correctBordereauSpelling(text: string): string {
  // Collapse the doubled vowel in the stem: BORDEREA(A…)U → BORDEREAU. The `A+` requires at
  // least one *extra* A, so correctly-spelled "BORDEREAU" is never touched.
  return text.replace(/(BORDEREA)A+(U)/gi, '$1$2');
}

// ─── Inventaire des pièces — droit public (#1704) ──────────────────────────
//
// Entre #1704 (2026-09-23) et #1786 (2026-09-28), les actes de droit public se terminaient par un
// « INVENTAIRE DES PIÈCES » (R. 412-2 CJA) dont chaque ligne COMMENCE par le numéro d'ordre :
// « 1 Carte d'identité ». Depuis #1786, un acte de droit public ne porte plus aucune liste
// (`documents/pieceNamingConvention.ts`, `stripAdministrativeInventory`) : l'inventaire est un
// document séparé. Ce bloc reste parce que les actes déjà générés, rouverts dans Word ou dans le
// canvas, portent encore la section : il la RECONNAÎT pour la mettre en forme comme un bordereau.

/**
 * « INVENTAIRE DES PIÈCES » (ou « INVENTAIRE DÉTAILLÉ DES PIÈCES », le vocabulaire exact de
 * R. 412-2). Capitales seules, comme BORDEREAU : un « l'inventaire des pièces » au fil du texte
 * n'est pas un titre.
 */
const INVENTAIRE_DES_PIECES_RE = /\bINVENTAIRE(?:\s+D[ÉE]TAILL[ÉE])?\s+DES\s+PI[EÈ]CES\b/;

/**
 * La ligne est-elle le TITRE d'un inventaire administratif ? Ancrée en tête (après `#`, gras
 * et balises), contrairement à `isBordereauLine` qui tolère le mot en milieu de ligne : ce
 * prédicat OUVRE la lecture « numéro nu = ligne de pièce », il doit donc être étroit.
 */
export function isInventaireHeadingLine(text: string): boolean {
  const cleaned = cleanBalises(text)
    .replace(/^#{1,6}\s*/, '')
    .replace(/\*\*|__/g, '')
    .trim();
  return /^INVENTAIRE(?:\s+D[ÉE]TAILL[ÉE])?\s+DES\s+PI[EÈ]CES\b/.test(cleaned);
}

/**
 * Ligne d'inventaire administratif : « 1 Carte d'identité », « - 12 Recours gracieux »,
 * « **5 bis Attestation** ». Numéro d'ordre en tête, une espace, puis le libellé.
 *
 * ⚠️ SANS ÉTAT, et c'est voulu : prise seule, cette forme matcherait « 12 mars 2024, le
 * préfet… » ou « 2 ans plus tard ». Elle ne vaut QUE sous un titre d'inventaire — les
 * appelants la combinent à l'état de section via `isPiecesLineInSection` (add-in) ou à
 * `inventaireActive` (parseur web). Ne JAMAIS l'ajouter à `isPiecesLine`, qui est lue partout.
 *
 * Volontairement étroite sur le séparateur : le format normalisé n'en a pas. « 1. Carte » reste
 * une liste numérotée markdown et « 1 - Carte » n'est pas reconnue — la normalisation de
 * l'add-in (Droit Public) les réécrit en amont en « 1 Carte ». Le libellé doit commencer par
 * une lettre, un guillemet, une parenthèse ou un crochet : « 1 000 euros » n'est pas une ligne
 * de pièce.
 *
 * Le crochet ouvrant compte (2026-09-23) : les trames d'acte de droit public sont écrites
 * « 1 [Décision administrative initialement attaquée] », et le modèle recopie ce squelette tel
 * quel quand il n'a pas de pièce à nommer. Sans `[`, la ligne retombait en texte courant alors
 * que sa forme d'avant la réécriture, « Pièce n°1 : [Décision…] », était bien stylée en pièce.
 */
export function isInventairePieceLine(text: string): boolean {
  const cleaned = cleanBalises(text).replace(/\*\*|__/g, '').trim();
  return /^(?:[-*•+]\s+)?\d{1,4}(?:\.\d{1,3})*(?:\s?(?:bis|ter|quater)\b|[a-z](?!\p{L}))?\s+[\p{L}«"“'([]/u.test(cleaned);
}

export function isBordereauLine(text: string): boolean {
  // Enhanced: Detect BORDEREAU and LISTE DES PIÈCES (with or without COMMUNIQUÉES) in both original format (with markers) and cleaned format
  // NOTE: Only uppercase BORDEREAU/LISTE should trigger this (not lowercase "bordereau")
  const cleaned = cleanBalises(text);

  // Pattern 1: Original format with ## or ** markers (case-sensitive) - COMMUNIQUÉES optional
  const originalPattern = /(?:^#{1,6}\s*)?(?:\*\*)?(?:BORDEREAU|LISTE\s+DES\s+PI[EÈ]CES(?:\s+COMMUNIQU[ÉE]ES)?)(?:\*\*)?/.test(cleaned);

  // Pattern 2: Cleaned format - direct BORDEREAU or LISTE DES PIÈCES detection (case-sensitive) - COMMUNIQUÉES optional
  const cleanedPattern = /^(?:BORDEREAU|LISTE)\s+DE[S]?\s+PI[EÈ]CES(?:\s+COMMUNIQU[ÉE]ES)?/.test(cleaned);

  // Pattern 3: Simple BORDEREAU or LISTE DES PIÈCES word detection (fallback, case-sensitive) - COMMUNIQUÉES optional
  const simplePattern = /\b(?:BORDEREAU|LISTE\s+DES\s+PI[EÈ]CES(?:\s+COMMUNIQU[ÉE]ES)?)\b/.test(cleaned);

  // Pattern 4 (#1704) : la liste de fin d'acte en droit public s'intitule « INVENTAIRE DES
  // PIÈCES » (R. 412-2 CJA), pas « BORDEREAU ». Même lecture que le pattern 3 (capitales
  // seules, n'importe où dans la ligne) pour que le titre reçoive exactement le traitement
  // d'un titre de bordereau : saut de page, style Hector-BORDEREAU, bascule des pièces.
  const inventairePattern = INVENTAIRE_DES_PIECES_RE.test(cleaned);

  // Robustness (2026-07-28): the redaction model systematically misspells the stem as
  // "BORDEREAAU". correctBordereauSpelling() fixes it upstream in the streaming buffer, but
  // this predicate is also reached on other paths (reformulation, direct insert), so two
  // anchored fallbacks keep detection working even if a heading typo slips through — without
  // false-positiving on body prose (both require an UPPERCASE heading):
  //  (a) an uppercase heading ending in "PIÈCES COMMUNIQUÉES/ANNEXÉES/…", whatever the
  //      spelling of "bordereau" before it;
  //  (b) an uppercase first word on the "BORDER" stem — no French heading other than
  //      bordereau/its typos begins that way ("BORDEAUX" fails at the R).
  const communiqueesHeading = /^#{0,6}\s*\*{0,2}[A-ZÀ-Ÿ'’ -]*PI[EÈ]CES\s+(?:COMMUNIQU[ÉE]ES|ANNEX[ÉE]ES|PRODUITES|JUSTIFICATIVES)\b/.test(cleaned);
  const bordereauStemHeading = /^#{0,6}\s*\*{0,2}BORDER[A-ZÀ-Ÿ]*/.test(cleaned);

  return originalPattern || cleanedPattern || simplePattern || inventairePattern || communiqueesHeading || bordereauStemHeading;
}

export function isBordereauHeading(text: string): boolean {
  // Enhanced: Detect BORDEREAU and LISTE DES PIÈCES headings with flexible patterns (case-sensitive) - COMMUNIQUÉES optional
  const cleaned = cleanBalises(text);
  return /^#{1,6}\s*(?:\*\*)?(?:BORDEREAU|LISTE\s+DES\s+PI[EÈ]CES(?:\s+COMMUNIQU[ÉE]ES)?|INVENTAIRE(?:\s+D[ÉE]TAILL[ÉE])?\s+DES\s+PI[EÈ]CES)(?:\*\*)?/.test(cleaned);
}

/**
 * Clean BORDEREAU and LISTE DES PIÈCES COMMUNIQUÉES text by removing ## and ** markdown markers
 */
export function cleanBordereauText(text: string): string {
  return text.trim()
    .replace(/^#{1,6}\s*/, '') // Remove heading markers
    .replace(/^\*\*/, '')      // Remove leading **
    .replace(/\*\*$/, '')      // Remove trailing **
    .trim();
}

/**
 * Clean Pièces text by removing ** markdown markers
 * 🔧 SMART CLEANUP: Only removes markers if they're malformed (odd count)
 * If valid pairs exist (even count), preserves them for bold processing
 */
export function cleanPiecesText(text: string): string {
  // 🔧 FIX: Always remove ALL ** markers from Pièces text
  // Bold formatting is applied programmatically in wordInsertion.ts (paragraph.font.bold = true)
  // Keeping ** markers causes them to appear literally in Word document
  return text.trim()
    .replace(/\*\*/g, '')
    .trim();
}

export function isPiecesLine(text: string): boolean {
  // 🔧 Strip ALL ** markers globally for detection (LLM often outputs **Pièce n°X**)
  // This is safe because we only use the cleaned text for pattern matching,
  // the original text with ** markers is preserved for actual formatting
  const cleaned = cleanBalises(text).replace(/\*\*/g, '').trim();

  // Une ligne de pièce = une ligne qui DÉBUTE par un renvoi de pièce. La grammaire vit
  // désormais dans `pieceCitation.ts` (source unique, table de vérité de 161 cas) et non plus
  // dans 10 regexes ancrées empilées ici — celles-ci ratraient `Pièces 1 & 2`, `Pièces 1 à 5`,
  // `Pièce nº 1`, `Pièce no 1`, `Pièce numéro 4` (mesuré : 1/8 des formes réelles), tout en
  // acceptant « Pièce maîtresse : … » (ex-pattern 4, classe de caractères OUVERTE),
  // « Pièce n°0 », « Pièce 2024 » et « Pièce n°IIII ».
  if (startsWithPieceCitation(cleaned) !== null) return true;

  // Ex-pattern 9, SEUL rescapé de l'ancienne liste : ligne de bordereau QUALIFIÉE mais sans
  // numéro (« Pièce jointe : le bail », « Pièce annexe : Plan »). `pieceCitation` exige un
  // numéro par construction — une citation sans numéro n'existe pas au fil du texte — mais au
  // niveau LIGNE ces formes sont de vraies lignes de pièce, et elles étaient reconnues avant.
  // Reproduit à l'identique : SINGULIER uniquement, sinon « PIÈCES JOINTES : » cesserait d'être
  // le seul verdict d'`isPiecesJointesBlock` (table de vérité add-in).
  return /^Pi[eè]ce\s+(jointe|annexe|communiqu[ée]e)\b/i.test(cleaned);
}

/**
 * Ligne de pièce COMPTE TENU DE LA SECTION : `isPiecesLine`, plus, sous un titre
 * « INVENTAIRE DES PIÈCES » uniquement, la ligne à numéro nu (`isInventairePieceLine`, #1704).
 *
 * Porte unique des appelants add-in qui connaissent la section (insertion Word, reformulation) :
 * le numéro nu n'est jamais lu hors inventaire, donc un acte civil — dont la liste s'intitule
 * BORDEREAU — garde exactement le verdict d'`isPiecesLine`, ligne pour ligne.
 */
export function isPiecesLineInSection(text: string, isUnderInventaire: boolean): boolean {
  return isPiecesLine(text) || (isUnderInventaire && isInventairePieceLine(text));
}

/**
 * Piece-side stamp — fixes a fast-streaming race (2026-07-30).
 *
 * A piece paragraph is styled "Pièce (corps)" when it sits ABOVE the bordereau heading, and
 * "Pièce (bord.)" when it sits under it. That side used to be decided at INSERTION time by
 * reading `BordereauStateManager.isInBordereauSection()` — but `enterBordereau()` is latched at
 * EXTRACTION time (streamingBuffer), and the SSE reader fires tokens fire-and-forget
 * (asyncJobService), so under a fast token rate (GLM/Tensorix) extraction races past the
 * bordereau heading and latches the flag while body pieces are still queued for insertion. Those
 * body pieces then read an already-set latch and get the wrong (under-bordereau) style. Sonnet's
 * slower stream hid it because insertion kept pace with extraction.
 *
 * Fix: capture the side at EXTRACTION, where stream order is authoritative, and carry it on the
 * paragraph as a `[[PIECE:BEFORE|AFTER]]` marker. `wordInserter` reads the marker instead of the
 * live latch, so the classification no longer depends on how far insertion lags extraction.
 */
export const PIECE_SIDE_MARKERS = { before: '[[PIECE:BEFORE]]', after: '[[PIECE:AFTER]]' } as const;

/** Prepend the piece-side stamp captured in stream order (`isUnderBordereau` at extraction time). */
export function stampPieceSide(content: string, isUnderBordereau: boolean): string {
  return (isUnderBordereau ? PIECE_SIDE_MARKERS.after : PIECE_SIDE_MARKERS.before) + content;
}

/**
 * Read and strip a leading `[[PIECE:BEFORE|AFTER]]` stamp. Returns the side (`null` when the
 * paragraph was never stamped — e.g. reformulation / direct-insert paths — so the caller falls
 * back to the live latch) and the text with the marker removed.
 */
export function readPieceSideStamp(text: string): { side: 'before' | 'after' | null; text: string } {
  const m = text.match(/^\[\[PIECE:(BEFORE|AFTER)\]\]/);
  if (!m) return { side: null, text };
  return { side: m[1] === 'AFTER' ? 'after' : 'before', text: text.slice(m[0]!.length) };
}

// ─── Parties et en-têtes ───────────────────────────────────────────────────

/**
 * Enhanced piece citation detection with comprehensive pattern matching
 * Handles various legal document piece citation formats including:
 * - Traditional numbered pieces (Pièce n°1, Pièce n°A1)
 * - Roman numeral pieces (Pièce n°I, Pièce n°II)
 * - Multi-level numbering (Pièce n°1.1, Pièce n°2.3.4)
 * - Date-based pieces (Pièce n°du 15/03/2024)
 * - Descriptive pieces (Pièce jointe, Pièce annexe)
 * - Both accented (Pièce) and non-accented (Piece) variations
 */
export function isPourBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?POUR\s*:(\*\*)?$/i.test(cleaned);
}

export function isContreBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?CONTRE\s*:(\*\*)?$/i.test(cleaned);
}

export function isEtContreBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?ET\s+CONTRE\s*:/i.test(cleaned);
}

export function isEtBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?ET\s*:(\*\*)?$/i.test(cleaned);
}

export function isDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?DE\s*:(\*\*)?$/i.test(cleaned);
}

export function isDevantBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?DEVANT\s*:(\*\*)?$/i.test(cleaned);
}

export function isParBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "PAR :"
  return /^(\*\*)?PAR\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "À :" marker (with accent majuscule)
 * This is a special delimiter in commandement de payer documents
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 */
export function isAMajusculeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "À :", with optional ** markers
  return /^(\*\*)?À\s*:(\*\*)?$/i.test(cleaned);
}

export function isPourPersonneBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "POUR MONSIEUR/MADAME" followed by any text (name and optional status)
  // Examples: "POUR MONSIEUR KARIM B, mis en examen", "POUR MADAME [NOM], Prévenue"
  // This pattern triggers bold, underlined, line breaks, and reduced spacing behavior
  return /^(\*\*)?POUR\s+(MONSIEUR|MADAME|M\.|MME\.?)\s+.+(\*\*)?$/i.test(cleaned);
}

export function isAuNomDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "AU NOM DE MONSIEUR/MADAME/M./MME ..." with optional colon
  return /^(\*\*)?AU\s+NOM\s+DE\s+(?:MONSIEUR|MADAME|M\.|MME\.?)\s+.+\s*:?(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "Demanderesse" or "Demanderesse." marker
 * This is a special delimiter in legal documents for the plaintiff party (feminine form)
 * NOTE: This marker is bold + italic + right-aligned with line breaks before and after
 */
export function isDemanderesseBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "Demanderesse", "Demanderesse.", with optional ** markers
  return /^(\*\*)?Demanderesse\.?(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "Défenderesse" or "Défenderesse." marker
 * This is a special delimiter in legal documents for the defendant party (feminine form)
 * NOTE: This marker is bold + italic + right-aligned with line breaks before and after
 */
export function isDefenderesseBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "Défenderesse", "Défenderesse.", "Defenderesse", "Defenderesse." with optional ** markers
  return /^(\*\*)?D[ée]fenderesse\.?(\*\*)?$/i.test(cleaned);
}

export function isRequerantBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?REQUÉRANT\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "DEMEURANT :" marker
 * This is a special delimiter in legal documents for address information
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 */
export function isDemeurantBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "DEMEURANT :", with optional ** markers
  return /^(\*\*)?DEMEURANT\s*:(\*\*)?$/i.test(cleaned);
}

export function isElisantDomicileBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "ÉLISANT DOMICILE en l'étude de :" or "ÉLISANT DOMICILE en l'étude de :"
  return /^(\*\*)?[ÉE]LISANT\s+DOMICILE\s+en\s+l['']étude\s+de\s*:(\*\*)?$/i.test(cleaned);
}

export function isPersonnesBlock(text: string): boolean {
  // Matches complex PERSONNES patterns like "PERSONNE(S) À L'ENCONTRE DE LAQUELLE (DESQUELLES) LA MESURE EST DEMANDÉE :"
  const cleaned = cleanBalises(text);
  return /^(\*\*)?PERSONNES?(\([^)]+\))?\s+À\s+L[''']ENCONTRE\s+DE\s+(LAQUELLE|LESQUELLES)\s*(\([^)]+\))?\s*(LA\s+MESURE\s+EST\s+DEMANDÉE)?\s*:(\*\*)?$/i.test(cleaned);
}

export function isDevantJugeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Pattern 1: DEVANT MONSIEUR/MADAME LE/LA JUGE DES RÉFÉRÉS/D'INSTRUCTION/DE L'EXÉCUTION DU TRIBUNAL/DE LA COUR ...
  // Pattern 2: DEVANT MONSIEUR/MADAME LE/LA JUGE DES CONTENTIEUX DE LA PROTECTION DU TRIBUNAL JUDICIAIRE ...
  // Allows optional city name (de Paris, de Roanne, etc.) and optional trailing text (sis [Adresse], etc.)
  return /^(\*\*)?DEVANT\s+(MONSIEUR|MADAME)\s+(LE\s+JUGE|LA\s+JUGE)\s+(DES\s+RÉFÉRÉS|D'INSTRUCTION|DE\s+L'EXÉCUTION|DES\s+CONTENTIEUX\s+DE\s+LA\s+PROTECTION)\s+(DU\s+TRIBUNAL|DE\s+LA\s+COUR)\s+(JUDICIAIRE|DE\s+GRANDE\s+INSTANCE|D'APPEL)(\s+DE\s+[\wÀ-ÿ\s-]+)?/i.test(cleaned);
}

export function isDecisionAttaqueeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?DÉCISION\s+ATTAQUÉE\s*:(\*\*)?$/i.test(cleaned);
}

export function isModalitesBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?MODALITÉS\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "CABINET D'AVOCATS" line (law firm header)
 * Should trigger a line break before to visually separate from previous content
 * Handles both straight and curly apostrophes
 */
export function isCabinetDAvocatsBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Support both apostrophe types: ' (straight) and ' (curly)
  return /^(\*\*)?CABINET\s+D['']AVOCATS(\*\*)?$/i.test(cleaned);
}

export function isAvocatPlaidantBlock(text: string): boolean {
  const cleaned = stripMarkdownFormatting(cleanBalises(text));
  return /^Ayant\s+pour\s+Avocat\s+(Plaidant|constitué)\s*:?$/i.test(cleaned);
}

export function isAvocatBlock(text: string): boolean {
  const cleaned = stripMarkdownFormatting(cleanBalises(text));
  return /^Ayant\s+pour\s+Avocat\s*:?$/i.test(cleaned) && !isAvocatPlaidantBlock(text);
}

/**
 * Detect "INFORMATIONS RELATIVES À LA RELATION DE TRAVAIL :" marker
 * This is a special delimiter in social/labor law documents
 * NOTE: This marker is bold + underlined with line breaks before and after
 */
export function isInformationsRelativesBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "INFORMATIONS RELATIVES À LA RELATION DE TRAVAIL :", with optional ** markers and accented variants
  return /^(\*\*)?INFORMATIONS\s+RELATIVES\s+[AÀ]\s+LA\s+RELATION\s+DE\s+TRAVAIL\s*:(\*\*)?$/i.test(cleaned);
}

export function isLAnDeuxMilleBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?L'AN\s+DEUX\s+MILLE(\*\*)?/i.test(cleaned);
}

/**
 * Detect "ET LE (date)" line in legal documents
 * This is a date continuation line that follows "L'AN DEUX MILLE..."
 * Examples: "ET LE QUINZE OCTOBRE", "ET LE PREMIER JANVIER", "ET LE VINGT-TROIS MARS"
 * NOTE: This marker is bold but has NO line breaks before or after (inline formatting only)
 */
export function isEtLeDateBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Match "ET LE" followed by a date (day word + optional month word)
  // French ordinal numbers: PREMIER, DEUX, TROIS, QUATRE, CINQ, SIX, SEPT, HUIT, NEUF, DIX, ONZE, DOUZE, TREIZE, QUATORZE, QUINZE, SEIZE, DIX-SEPT, DIX-HUIT, DIX-NEUF, VINGT, VINGT-ET-UN, VINGT-DEUX, etc., TRENTE, TRENTE-ET-UN
  // French months: JANVIER, FÉVRIER, MARS, AVRIL, MAI, JUIN, JUILLET, AOÛT, SEPTEMBRE, OCTOBRE, NOVEMBRE, DÉCEMBRE
  return /^(\*\*)?ET\s+LE\s+[A-ZÀ-ÿ-]+(\s+[A-ZÀ-ÿ]+)?(\*\*)?$/i.test(cleaned);
}

/**
 * Detect article references (e.g., "Art. 643 :", "ARTICLE 123 :")
 * These should be formatted as bold and NOT italic, even if inside a quote
 */
export function isArticleReference(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(Art\.?|ARTICLE)\s+[A-Za-zÀ-ÿ0-9.\-§()/\s]*?[:\s]/i.test(cleaned.trim());
}

// ─── Formules d'huissier, commandement et assignation ──────────────────────

export function isPourDesFailsDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "Pour des faits de :", "**Pour des faits de :**", etc.
  return /^(\*\*)?Pour\s+des\s+faits\s+de\s*:(\*\*)?$/i.test(cleaned);
}

export function isALaRequeteDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches all possible combinations of accented/non-accented variants
  return /^(\*\*)?[AÀ]\s+LA\s+REQU[EÊ]TE\s+DE\s*:(\*\*)?$/i.test(cleaned);
}

export function isALAttentionDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches all possible combinations of accented/non-accented variants
  // Supports both straight apostrophe (') and curly apostrophe (')
  return /^(\*\*)?[AÀ]\s+L['']ATTENTION\s+DE\s*:(\*\*)?$/i.test(cleaned);
}

export function isALaDemandeDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "À LA DEMANDE DE :" with optional ** markers and accented/non-accented variants
  return /^(\*\*)?[AÀ]\s+LA\s+DEMANDE\s+DE\s*:(\*\*)?$/i.test(cleaned);
}

export function isDeLaPartDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "DE LA PART DE :"
  return /^(\*\*)?DE\s+LA\s+PART\s+DE\s*:(\*\*)?$/i.test(cleaned);
}

export function isDuChefDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  return /^(\*\*)?DU\s+CHEF\s+DE\s*:(\*\*)?$/i.test(cleaned);
}

export function isMisEnExamenDuChefDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "Mis en examen du chef de :"
  return /^(\*\*)?Mis\s+en\s+examen\s+du\s+chef\s+de\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "J'AI, commissaire de justice soussigné, DONNÉ ASSIGNATION À :" marker
 * This is the modern equivalent of the huissier assignation formula
 * Handles both straight and curly apostrophes
 */
export function isCommissaireJusticeDonneAssignationBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "J'AI, commissaire de justice soussigné, DONNÉ ASSIGNATION À :"
  // With flexible apostrophe handling and optional ** markers
  return /^(\*\*)?J[''`′']AI,?\s*commissaire\s+de\s+justice\s+soussign[ée],?\s+DONN[ÉE]\s+ASSIGNATION\s+[AÀ]\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "COMMISSAIRE DE JUSTICE :" marker
 * This is a legal document marker indicating the commissaire de justice (modern bailiff)
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 */
export function isCommissaireDeJusticeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "COMMISSAIRE DE JUSTICE :", "COMMISSAIRE DE JUSTICE:" with optional ** markers
  return /^(\*\*)?COMMISSAIRE\s+DE\s+JUSTICE\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "J'AI, MAITRE :" marker
 * This is a legal document formula indicating the bailiff (huissier) action
 * Handles both straight and curly apostrophes, with flexible spacing
 * NOTE: This marker is bold only (NO underline), line break BEFORE and AFTER
 */
export function isJAiMaitreBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "J'AI, MAITRE :", "J'AI, MAÎTRE :", with optional ** markers
  // Supports: straight ('), curly right ('), curly left ('), grave (`), prime (′)
  return /^(\*\*)?J[''`′']AI,?\s*MA[IÎ]TRE\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "J'ai," marker (commandement de payer avec clause résolutoire)
 * This is a special delimiter in legal documents for the commissaire de justice action
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 * Handles both straight and curly apostrophes
 */
export function isJAiBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "J'ai,", "J'AI,", with optional ** markers and flexible apostrophes
  // Supports: straight ('), curly right ('), curly left ('), grave (`), prime (′)
  return /^(\*\*)?J[''`′']ai,(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "J'AI" standalone marker (like POUR : and CONTRE :)
 * This is a special delimiter in legal documents, similar to POUR and CONTRE
 * NOTE: This marker is bold + underline, with line break BEFORE and AFTER
 * Handles both straight and curly apostrophes
 * Supports: "J'AI", "J'AI :", "J'ai", "J'ai :" (with or without colon)
 */
export function isJAiStandaloneBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "J'AI", "J'AI :", "J'ai", "J'ai:", with optional ** markers and flexible apostrophes
  // Supports: straight ('), curly right ('), curly left ('), grave (`), prime (′)
  // The colon is now OPTIONAL to match "J'AI" alone
  return /^(\*\*)?J[''`′'][Aa][Ii]\s*:?(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "L'HONNEUR D'INFORMER :" marker
 * This is a special delimiter in legal documents for the notification section
 * NOTE: This marker is bold only (NO underline), line break AFTER only (not before)
 * Handles both straight and curly apostrophes
 */
export function isLHonneurDInformerBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "L'HONNEUR D'INFORMER :", with optional ** markers and flexible apostrophes
  return /^(\*\*)?L['']HONNEUR\s+D['']INFORMER\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "Donné commandement," marker
 * This is a special delimiter in legal documents for the commandement action
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 */
export function isDonneCommandementBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "Donné commandement,", "DONNÉ COMMANDEMENT,", with optional ** markers
  return /^(\*\*)?Donn[ÉEée]\s+commandement,(\*\*)?$/i.test(cleaned);
}

export function isAiDonneCommandementBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "AI DONNÉ COMMANDEMENT À :"
  return /^(\*\*)?AI\s+DONN[ÉE]\s+COMMANDEMENT\s+[AÀ]\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "De payer les sommes suivantes :" marker
 * This is a special delimiter in legal documents for the payment demand section
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 */
export function isDePayerLesSommesSuivantesBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "De payer les sommes suivantes :", with optional ** markers
  return /^(\*\*)?De\s+payer\s+les\s+sommes\s+suivantes\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "TOTAL DÛ :" marker (followed by amount)
 * This marker should EXIT reduced spacing mode for the rest of the document
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 * Pattern matches "TOTAL DÛ : XXX €" where XXX is the amount
 */
export function isTotalDuBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "TOTAL DÛ : 3 000,00 €", "Total dû : XXX", with optional ** markers
  // The line STARTS with TOTAL DÛ : (not necessarily ends with it)
  return /^(\*\*)?Total\s+d[ÛUûu]\s*:/i.test(cleaned);
}

/**
 * Detect "En cas de cautionnement :" marker
 * This is a special delimiter in commandement de payer documents
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 */
export function isEnCasDeCautionnementBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "En cas de cautionnement :", with optional ** markers
  return /^(\*\*)?En\s+cas\s+de\s+cautionnement\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "Vous êtes tenu :" marker
 * This is a special delimiter in assignation documents
 * NOTE: This marker is bold only (NO underline), line break AFTER only
 */
export function isVousEtesTenuBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches: "Vous êtes tenu :", with optional ** markers and accent variations
  return /^(\*\*)?Vous\s+[êe]tes\s+tenu\s*:(\*\*)?$/i.test(cleaned);
}

export function isVisantClauseResolutoireBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "VISANT LA CLAUSE RÉSOLUTOIRE DU BAIL" (with optional colon)
  return /^(\*\*)?VISANT\s+LA\s+CLAUSE\s+R[ÉE]SOLUTOIRE\s+DU\s+BAIL\s*:?(\*\*)?$/i.test(cleaned);
}

export function isAComparaitreLeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "À COMPARAÎTRE LE" followed by any text (date, time, placeholder [DATE ET HEURE], or colon)
  // Supports accented variants (A/À, I/Î)
  // Examples: "À COMPARAÎTRE LE [DATE ET HEURE]", "À COMPARAÎTRE LE 15 janvier 2025", "À COMPARAÎTRE LE :"
  return /^(\*\*)?[AÀ]\s+COMPARA[IÎ]TRE\s+LE\b/i.test(cleaned);
}

export function isDAvoirAComparaitreBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Supports straight or curly apostrophe and accented variations
  return /^(\*\*)?D['’]AVOIR\s+À\s+COMPARA[IÎ]TRE\s*:(\*\*)?$/i.test(cleaned);
}

/**
 * Detect "D'AVOIR À CONSTITUER OBLIGATOIREMENT AVOCAT POUR L'AUDIENCE DÉMATÉRIALISÉE DU..."
 * This is a special delimiter in legal assignment documents
 * NOTE: This marker is bold, with line break BEFORE and AFTER
 * The date/time part at the end is variable (can be placeholder or actual date)
 */
export function isDAvoirAConstituerAvocatBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Supports straight or curly apostrophe
  // Matches: "D'AVOIR À CONSTITUER OBLIGATOIREMENT AVOCAT POUR L'AUDIENCE DÉMATÉRIALISÉE DU [...]"
  return /D['']AVOIR\s+[AÀ]\s+CONSTITUER\s+OBLIGATOIREMENT\s+AVOCAT/i.test(cleaned);
}

export function isAssisteDeBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "ASSISTÉ(E) DE :", "ASSISTÉE DE :", or "ASSISTÉ DE :"
  // The \([ÉE]\) part (parentheses with E/É inside) is now optional
  return /^(\*\*)?ASSIST[ÉE]+(?:\s*\([ÉE]\))?\s+DE\s*:(\*\*)?$/i.test(cleaned);
}

export function isPourEtreEntenduBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "POUR ÊTRE ENTENDU À L'AUDIENCE DU :"
  return /^(\*\*)?POUR\s+[ÊE]TRE\s+ENTENDU\s+[AÀ]\s+L['']AUDIENCE\s+DU\s*:(\*\*)?$/i.test(cleaned);
}

export function isAvecConstitutionBlock(text: string): boolean {
  const cleaned = cleanBalises(text);
  // Matches "AVEC CONSTITUTION DE PARTIE CIVILE"
  return /^(\*\*)?AVEC\s+CONSTITUTION\s+DE\s+PARTIE\s+CIVILE(\*\*)?$/i.test(cleaned);
}

/**
 * Check if text is any document structure marker (excluding PLAISE, BORDEREAU, Pieces)
 */
export function isAnyDocumentMarker(text: string): boolean {
  const cleaned = cleanBalises(text);

  // Primary markers
  if (isPourBlock(text) || isPourPersonneBlock(text) || isContreBlock(text) || isJAiStandaloneBlock(text) || isInformationsRelativesBlock(text) || isPourDesFailsDeBlock(text) || isDeBlock(text) || isDevantBlock(text) || isObjetBlock(text) || isEtBlock(text) || isALaRequeteDeBlock(text) || isALAttentionDeBlock(text) || isDuChefDeBlock(text) || isCommissaireJusticeDonneAssignationBlock(text) || isCommissaireDeJusticeBlock(text) || isALaDemandeDeBlock(text) || isDeLaPartDeBlock(text) || isVisantClauseResolutoireBlock(text) || isAiDonneCommandementBlock(text) || isAComparaitreLeBlock(text) || isAssisteDeBlock(text) || isAuNomDeBlock(text) || isParBlock(text) || isMisEnExamenDuChefDeBlock(text) || isElisantDomicileBlock(text) || isPourEtreEntenduBlock(text) || isAvecConstitutionBlock(text) || isTresImportantBlock(text) || isDAvoirAComparaitreBlock(text) || isDemeurantBlock(text) || isJAiBlock(text) || isDonneCommandementBlock(text) || isDePayerLesSommesSuivantesBlock(text) || isTotalDuBlock(text) || isAMajusculeBlock(text) || isEnCasDeCautionnementBlock(text) || isObjetDeLaDemandeBlock(text) || isVousEtesTenuBlock(text)) {
    return true;
  }

  // Party identification markers
  const isPlaignantContent = /^(\*\*)?PLAIGNANT\s*:(\*\*)?$/i.test(cleaned);
  const isCitantContent = /^(\*\*)?CITANT\s*:(\*\*)?$/i.test(cleaned);
  const isCiteContent = /^(\*\*)?CITÉ(\s*\([^)]+\))?\s*:/i.test(cleaned);
  // Match DEMANDEUR / DÉFENDEUR / DÉFENDERESSE — uppercase party labels with colon.
  const isDemandeurContent = /^(\*\*)?(DEMANDEUR|DEMANDEUSE|D[ÉE]FENDEUR|D[ÉE]FENDERESSE)\s*:(\*\*)?$/i.test(cleaned);
  const isDestinataireContent = /^(\*\*)?DESTINATAIRE\s*:(\*\*)?$/i.test(cleaned);

  if (isPlaignantContent || isCitantContent || isCiteContent || isDemandeurContent || isDestinataireContent) {
    return true;
  }

  // Legal procedure markers
  const isALaRequeteDeContent = /^(\*\*)?A LA REQUETE DE\s*:(\*\*)?$/i.test(cleaned);
  const isALEncotreDeContent = /^(\*\*)?À L'ENCONTRE DE\s*:(\*\*)?$/i.test(cleaned);
  const isALaRequeteDeOfficialContent = /^(\*\*)?À LA REQUÊTE DE\s*:(\*\*)?$/i.test(cleaned);
  const isEntreContent = /^(\*\*)?ENTRE\s*:(\*\*)?$/i.test(cleaned);
  const isEntreLesContent = /^(\*\*)?ENTRE LES SOUSSIGNÉS\s*:(\*\*)?$/i.test(cleaned);
  const isEtContent = isEtBlock(text);

  if (isALaRequeteDeContent || isALEncotreDeContent || isALaRequeteDeOfficialContent || isEntreContent || isEntreLesContent || isEtContent) {
    return true;
  }

  // Administrative/service markers
  const isDateEtLieuContent = /^(\*\*)?DATE ET LIEU\s*:(\*\*)?$/i.test(cleaned);
  const isModalitesEnvoiContent = /^(\*\*)?MODALITÉS D'ENVOI\s*:(\*\*)?$/i.test(cleaned);
  const isModalitesContent = /^(\*\*)?MODALITÉS\s*:(\*\*)?$/i.test(cleaned);
  const isExpediteurContent = /^(\*\*)?EXPÉDITEUR\s*:(\*\*)?$/i.test(cleaned);

  if (isDateEtLieuContent || isModalitesEnvoiContent || isModalitesContent || isExpediteurContent) {
    return true;
  }

  // Legal service markers
  const isJAiDonneAssignationContent = /^(\*\*)?J'AI DONNÉ ASSIGNATION À\s*:(\*\*)?$/i.test(cleaned);
  const isJAiHuissierContent = /^(\*\*)?J'AI, HUISSIER\b(\*\*)?/i.test(cleaned);
  const isJAiMaitreContent = isJAiMaitreBlock(text);
  const isDonneAssignationAContent = /^(\*\*)?DONNÉ ASSIGNATION À\s*:(\*\*)?$/i.test(cleaned);
  const isAComparaitreLeContent = /^(\*\*)?À COMPARAÎTRE LE\s*:(\*\*)?$/i.test(cleaned);
  const isTresImportantContent = /^(\*\*)?TRÈS IMPORTANT\s*:(\*\*)?$/i.test(cleaned);

  if (isJAiDonneAssignationContent || isJAiHuissierContent || isJAiMaitreContent || isDonneAssignationAContent || isAComparaitreLeContent || isTresImportantContent) {
    return true;
  }

  // L'HONNEUR D'INFORMER marker
  if (isLHonneurDInformerBlock(text)) {
    return true;
  }

  // Legal representation and date markers
  if (isAvocatPlaidantBlock(text) || isAvocatBlock(text) || isLAnDeuxMilleBlock(text) || isEtLeDateBlock(text) || isMadameMonsieurBlock(text) || isEtContreBlock(text) || isDecisionAttaqueeBlock(text) || isModalitesBlock(text) || isRequerantBlock(text) || isPersonnesBlock(text) || isDevantJugeBlock(text)) {
    return true;
  }

  return false;
}

// ─── Variantes ANCRÉES — la lecture du canvas ───────────────────────────────
//
// Deux lectures du même vocabulaire de marqueurs, et il faut les deux (#1001).
//
// L'add-in Word lit les marqueurs À PRÉFIXE : `isObjetBlock('OBJET : recouvrement…')` est vrai.
// C'est correct chez lui, parce qu'un marqueur y sert de DÉCLENCHEUR (poser du gras, ouvrir une
// bascule d'état) et qu'un faux positif se paie en graisse superflue, rien de plus.
//
// Le canvas lit les marqueurs ANCRÉS : le marqueur doit être SEUL sur sa ligne. C'est correct
// chez lui aussi, et pour deux raisons qui ne se négocient pas :
//   1. ses règles portent `pageBreakBefore` — un « le BORDEREAU communiqué » en plein corps
//      provoquerait un saut de page au milieu de l'acte ;
//   2. le mode compact de l'en-tête de courrier repose sur le NON-match : « OBJET : recouvrement
//      de la créance … » ne doit matcher aucune règle, précisément pour que
//      `turnsOnReducedSpacing` s'allume et que l'en-tête sorte serré. Un marqueur qui matche
//      trop large éteint le mécanisme (régression mesurée le 2026-08-16 sur les gardes live).
//
// On ne peut pas dériver mécaniquement la forme ancrée de la forme à préfixe : les prédicats de
// l'add-in ne disent pas OÙ finit le marqueur qu'ils ont reconnu, et rien dans la ligne ne
// permet de le retrouver (« J'AI DONNÉ ASSIGNATION À : » doit matcher, « J'AI DONNÉ ASSIGNATION
// À : Monsieur X » non — or les deux commencent par « J'AI », que `isJAiStandaloneBlock` accepte
// déjà seul). La forme ancrée est donc écrite explicitement, ici, COLLÉE à la forme à préfixe :
// les deux lectures se lisent d'un seul coup d'œil, dans un seul fichier, et un marqueur ajouté
// se voit immédiatement manquant dans l'autre. C'est ce que la duplication add-in ↔ web ne
// permettait pas — elle vivait dans deux applications que personne ne relit ensemble.
//
// ⚠️ Ces fonctions sont la référence du CANVAS. Elle est verrouillée par
// `marqueurs-reference-web.test.ts`, elle-même figée sur le comportement validé par les gardes
// Playwright live. En cas de conflit avec un prédicat add-in, c'est la garde live qui tranche.

/** PLAISE AU TRIBUNAL … — reconnu en tête de ligne (le reste est le nom de la juridiction). */
export function estPlaiseAncre(ligne: string): boolean {
  return /^(\*\*)?PLAISE\b/i.test(ligne);
}

/** INFORMATIONS RELATIVES À LA RELATION DE TRAVAIL — actes prud'homaux. */
export function estInformationsRelativesAncre(ligne: string): boolean {
  return /^(\*\*)?INFORMATIONS\s+RELATIVES\s+[AÀ]\s+LA\s+RELATION\s+DE\s+TRAVAIL\s*:?(\*\*)?\s*$/i.test(
    ligne,
  );
}

/** POUR MONSIEUR / POUR MADAME <Nom> — intitulé de partie avec nom du destinataire. */
export function estPourPersonneAncre(ligne: string): boolean {
  return /^(\*\*)?POUR\s+(MONSIEUR|MADAME)\s+\S/i.test(ligne);
}

/**
 * BORDEREAU / LISTE DES PIÈCES / INVENTAIRE DES PIÈCES (#1704, droit public) — en tête de
 * ligne ET ligne entièrement en capitales. La contrainte de casse est la garde qui empêche une
 * phrase de corps citant le bordereau (ou l'inventaire) de déclencher un saut de page.
 */
export function estBordereauAncre(ligne: string): boolean {
  const sansGras = ligne.replace(/\*\*/g, '');
  return (
    /^(BORDEREAU|LISTE DES PI[EÈ]CES|INVENTAIRE(?: D[ÉE]TAILL[ÉE])? DES PI[EÈ]CES)\b/i.test(sansGras)
    && sansGras === sansGras.toUpperCase()
  );
}

/**
 * PIÈCES JOINTES — deux-points FACULTATIF, contrairement à `isPiecesJointesBlock` côté add-in
 * qui l'exige. Les modèles écrivent l'un ou l'autre ; côté canvas la ligne porte un saut de page,
 * le perdre décale toute la fin du document (régression mesurée le 2026-08-16).
 */
export function estPiecesJointesAncre(ligne: string): boolean {
  return /^(\*\*)?PI[EÈ]CES JOINTES/i.test(ligne);
}

/** PAR CES MOTIFS. */
export function estParCesMotifsAncre(ligne: string): boolean {
  return /^(\*\*)?PAR CES MOTIFS/i.test(ligne);
}

/** TRÈS IMPORTANT — seul sur sa ligne. */
export function estTresImportantAncre(ligne: string): boolean {
  return /^(\*\*)?TR[EÈ]S\s+IMPORTANT\s*:?(\*\*)?$/i.test(ligne);
}

/** POUR : — intitulé de partie nu. */
export function estPourAncre(ligne: string): boolean {
  return /^(\*\*)?POUR\s*:(\*\*)?$/i.test(ligne);
}

/** CONTRE : / DEVANT : — intitulés de partie nus. */
export function estContreOuDevantAncre(ligne: string): boolean {
  return /^(\*\*)?(CONTRE|DEVANT)\s*:(\*\*)?$/i.test(ligne);
}

/**
 * DEVANT MONSIEUR/MADAME LE JUGE … — y compris la forme abrégée « DEVANT MONSIEUR LE JUGE : ».
 * Plus large que `isDevantJugeBlock`, qui exige la formule complète avec la juridiction.
 */
export function estDevantJugeAncre(ligne: string): boolean {
  return /^(\*\*)?DEVANT\s+(MONSIEUR|MADAME)\s+(LE|LA)\s+(JUGE|PR[EÉ]SIDENT)/i.test(ligne);
}

/** DEMANDERESSE / DÉFENDERESSE / REQUÉRANT(E) / DEMANDEUR(S) / DÉFENDEUR(S) — seuls sur leur ligne. */
export function estLabelDePartieAncre(ligne: string): boolean {
  return /^(\*\*)?(DEMANDERESSE|D[EÉ]FENDERESSE|REQU[EÉ]RANT(E)?|D[EÉ]FENDEUR(S)?|DEMANDEUR(S)?)(\*\*)?\s*$/i.test(
    ligne,
  );
}

/**
 * Salutation « Madame, Monsieur, » et ses variantes.
 *
 * `[\s,;:]*` absorbe la ponctuation entre et après les civilités ; le `$` final est
 * LOAD-BEARING — sans lui, « MONSIEUR Dupont, demeurant … » deviendrait un marqueur.
 * Plus large que `isMadameMonsieurBlock` (qui exige littéralement « Madame, Monsieur ») sur la
 * civilité seule, et plus stricte sur le suffixe.
 */
export function estSalutationAncre(ligne: string): boolean {
  return /^(\*\*)?A?\s*(MADAME|MONSIEUR)\b[\s,;:]*(ET\s+)?((MADAME|MONSIEUR)\b[\s,;:]*)?(\*\*)?\s*$/i.test(
    ligne,
  );
}

/**
 * Les marqueurs de bloc gras, seuls sur leur ligne (deux-points facultatif).
 *
 * C'est la contrepartie ancrée de `isAnyDocumentMarker`. Le vocabulaire est le même à quelques
 * entrées près : on trouve ici des rubriques de contrat et de plan (« D'UNE PART », « PRÉAMBULE »,
 * « FIN ») que le pipeline Word ne traite pas comme des marqueurs de bloc, et il manque ici
 * quelques formules d'huissier que l'add-in reconnaît mais que le canvas n'a jamais stylées.
 * Aligner les deux listes changerait le rendu des actes — c'est un arbitrage produit, pas un
 * nettoyage : à faire entrée par entrée, avec les deux tables de vérité comme filet.
 */
export function estMarqueurDeBlocAncre(ligne: string): boolean {
  return /^(\*\*)?(OBJET|[AÀ] LA REQU[EÊ]TE DE|[AÀ] L'ATTENTION DE|[AÀ] L'ENCONTRE DE|[AÀ] LA DEMANDE DE|[AÀ] LA REQU[EÊ]TE DES|J'AI DONN[EÉ] ASSIGNATION [AÀ]|J'AI DONN[EÉ] COMMANDEMENT [AÀ]|J'AI HUISSIER|J'AI COMMISSAIRE DE JUSTICE|J'AI(?:\s|$)|ENTRE LES SOUSSIGN[EÉ]S?|ENTRE|ET CONTRE|AYANT POUR AVOCAT|D[EÉ]MEURANT|EXP[EÉ]DITEUR|DESTINATAIRE|DATE ET LIEU|MODALIT[EÉ]S D'ENVOI|D[EÉ]CISION ATTAQU[EÉ]E|MIS EN EXAMEN DU CHEF DE|[EÉ]LISANT DOMICILE|DU CHEF DE|DE LA PART DE|AU NOM DE|D'UNE PART|D'AUTRE PART|POUR DES FAITS DE|PERSONNES [AÀ] L'ENCONTRE DE|PR[EÉ]AMBULE|EXPOS[EÉ] DES FAITS|RAPPEL DES FAITS|FIN|PLAIGNANT|CITANT|CIT[EÉ]|[AÀ] COMPARA[ÎI]TRE LE|D'AVOIR [AÀ] COMPARA[ÎI]TRE|D'AVOIR [AÀ] CONSTITUER OBLIGATOIREMENT AVOCAT|ASSIST[EÉ]\(E\) DE|ASSIST[EÉ]E? DE|AVEC CONSTITUTION DE PARTIE CIVILE)\s*:?(\*\*)?\s*$/i.test(
    ligne,
  );
}

/**
 * « Pièce n°X : … » — la forme que le CANVAS style (`Hector-Piece-Before`).
 *
 * Porte DISTINCTE d'`isPiecesLine` (porte add-in Word), et volontairement plus étroite : le
 * canvas ne style que la LIGNE DÉDIÉE de bordereau, jamais un renvoi au fil du texte. Le
 * **deux-points est donc obligatoire** — c'est lui qui sépare « Pièce n°3 : Attestation »
 * (ligne de bordereau) de « Pièce n°A1bis » (renvoi nu, qui reste `Hector-Normal`). Cet
 * arbitrage produit est figé par `marqueurs-reference-web.test.ts` ; ne pas aligner les deux
 * portes sans le rejouer.
 *
 * Depuis 2026-09-03, la reconnaissance du renvoi lui-même délègue à `pieceCitation.ts` : la
 * regex maison ne voyait que `Pièce n°<chiffres>`, donc ni `Pièces 1 et 2 :`, ni
 * `Pièce N°1 & 2 :`, ni `Pièce adverse n°3 :`, ni `Pièce n°A1bis :` n'étaient stylées.
 */
export function estPieceAncre(ligne: string): boolean {
  const citation = startsWithPieceCitation(ligne);
  if (citation === null) return false;
  // Le deux-points doit suivre IMMÉDIATEMENT le renvoi (espaces tolérés) — pas n'importe où
  // dans la ligne, sinon « Pièce n°3 démontre que : … » deviendrait une ligne de bordereau.
  return /^\s*:/.test(ligne.normalize('NFC').slice(citation.end));
}

/**
 * « Par lettre recommandée … » — en tête de ligne, suffixe libre (« avec avis de réception »).
 * Plus stricte que `isParLettreRecommandeeBlock`, qui accepte aussi « Par courrier … ».
 */
export function estEnvoiRecommandeAncre(ligne: string): boolean {
  return /^(\*\*)?PAR LETTRE RECOMMAND[EÉ]E\b.*$/i.test(ligne);
}

/**
 * En-tête de bordereau, d'inventaire ou de pièces jointes, pour le chemin des headings markdown.
 * INVENTAIRE est lu en capitales seules (#1704) : un « ## Inventaire des pièces » en casse de
 * titre reste un heading, comme il l'était avant l'arrivée de l'inventaire administratif.
 */
export function estEnTeteBordereauAncre(ligne: string): boolean {
  return (
    /^(BORDEREAU|LISTE DES PI[EÈ]CES|PI[EÈ]CES JOINTES)\b/i.test(ligne)
    || /^INVENTAIRE(?: D[ÉE]TAILL[ÉE])? DES PI[EÈ]CES\b/.test(ligne)
  );
}

const ADMINISTRATIVE_PIECE_LABEL_MAX_LENGTH = 200;
// Amorces d'un moyen (« Sur », « Quant à », « En ce qui concerne ») ou verbes du dispositif des
// conclusions (« Annuler », « Condamner »…) — jamais le premier mot d'un intitulé de pièce.
const ADMINISTRATIVE_NON_PIECE_OPENING_RE =
  /^(?:Sur|Quant|En\s+ce\s+qui|À\s+titre|A\s+titre|Annuler|Condamner|Enjoindre|Mettre|Rejeter|Ordonner|Dire|Juger|Constater|D[ée]clarer|R[ée]former|Prononcer|Suspendre|Accorder|Allouer|Surseoir|Renvoyer|Reconna[iî]tre)(?!\p{L})/iu;

/**
 * Paragraphe-renvoi à une pièce dans un acte de droit public (#1786) : « 3 - Délibération n°
 * 2024-047 du 4 avril 2024 » — numéro d'ordre, tiret, intitulé. C'est la forme que l'avocat de
 * contentieux administratif place sous le paragraphe qui s'appuie sur la pièce, à la place de
 * « Pièce n°3 : … ».
 *
 * Réservé aux actes de convention ADMINISTRATIVE : l'appelant ne l'interroge qu'en droit public,
 * car un acte civil peut légitimement contenir un paragraphe « 1 - Sur la recevabilité ». Le
 * libellé doit commencer par une lettre, un guillemet, une parenthèse ou un crochet : « 3 - 4 »
 * n'est pas une pièce.
 *
 * Même en droit public, un acte numérote aussi ses moyens (« 1 - Sur la recevabilité de la
 * requête ») et ses conclusions (« 1 - Annuler l'arrêté du 12 mars 2024 ; ») sous cette forme.
 * Revue #1791 (2026-09-29) : ces lignes recevaient le style de pièce (gras, aligné à droite). Un
 * intitulé de pièce est un groupe nominal court : on refuse une amorce de moyen ou un infinitif de
 * conclusions, et une ligne qui finit comme un élément d'énumération (« ; », « : », « , », « . »).
 */
export function isAdministrativePieceReferenceLine(text: string): boolean {
  const cleaned = cleanBalises(text).replace(/\*\*|__/g, '').trim();
  const match = /^(?:[-*•+]\s+)?\d{1,4}(?:[.-]\d{1,3})*(?:\s?(?:bis|ter|quater)\b)?\s+-\s+([\p{L}«"“'([].*)$/u.exec(cleaned);
  const label = match?.[1]?.trim();
  if (!label) return false;
  return label.length <= ADMINISTRATIVE_PIECE_LABEL_MAX_LENGTH
    && !/[;:,.]$/u.test(label)
    && !ADMINISTRATIVE_NON_PIECE_OPENING_RE.test(label);
}

